import { KorpoEmployerFundData, ProductType, RegisterMode, UnitCategoryCode } from 'cart-model';
import { Formik, FormikErrors, FormikHelpers, FormikTouched, useField } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router';
import {
  Button,
  Radio,
  TextField,
  RadioGroup,
  Checkbox,
  FormControlLabel,
  Label,
  Typography,
  Card
} from 'ui-components';
import * as Yup from 'yup';
import { getApiEmployerProductFunds, postKorpoCode } from '../../api/korpoApi';
import { ApiKorpoCodeResponseStatus, validKorpoCodeResponseStatus } from '../../api/model/ApiKorpoCodeResponse';
import ProductWizardState from '../../model/ProductWizardState';
import { cartStateSlice, productWizardActions } from '../../model/store';
import { ProductWizardTitle } from '../ProductWizard/ProductWizardTitle/ProductWizardTitle';
import Screen from '../ProductWizard/Screen';
import { useRouter, useUniqueWcagValues } from 'common-react';
import { CustomForm } from '../formik/CustomForm/CustomForm';
import _ from 'lodash';

import useStyles from './OpenIkeIkzeKorpo.style';

export const FINANCIAL_INSTITUTION_REGEX = /^[\p{Script=Latin}0-9 \-.,`'"&!@+()/]*$/u;
export const FINANCIAL_INSTITUTION_MIN_LENGTH = 3;
export const FINANCIAL_INSTITUTION_MAX_LENGTH = 255;

export enum OpenIkeIkzeKorpoOperationType {
  open = 'open',
  transfer = 'transfer',
  ppe_transfer = 'ppe_transfer' }

export interface OpenIkeIkzeKorpoDataForm {
  korpoCode: string;
  operationType: OpenIkeIkzeKorpoOperationType;
  disclaimer?: boolean;
  financialInstitution?: string;
}

const OpenIkeIkzeKorpo: React.FC = () => {
  const c = useStyles({});
  const [t] = useTranslation('open-ike-ikze-korpo');
  const [tt] = useTranslation('translation');
  const dispatch = useDispatch();
  const productType = useSelector((state: ProductWizardState) => state.uiState.productType);
  const allFunds = useSelector((state: ProductWizardState) => state.apiState.funds);
  const [url] = useTranslation('url');
  const { history } = useRouter();
  const { getUniqueValue } = useUniqueWcagValues();
  const openIkeIkzeKorpoRadioGroup = getUniqueValue('open-ike-ikze-korpo-radio-group');
  if (productType === undefined || productType === ProductType.funds) {
    return <Redirect to="/"/>;
  }

  const validationSchema = Yup.object<OpenIkeIkzeKorpoDataForm>().shape({
    korpoCode: Yup.string().trim().required(),
    operationType: Yup.mixed<OpenIkeIkzeKorpoOperationType>()
                      .oneOf(Object.values(OpenIkeIkzeKorpoOperationType))
                      .required(t('validation.at-least-one-option-required')),
    disclaimer: Yup.boolean().when('operationType', {
      is: 'transfer',
      then: Yup.boolean().equals([true], t('validation.required')),
      otherwise: Yup.boolean().notRequired() }),
    financialInstitution: Yup.string().when('operationType', {
      is: 'transfer',
      then: Yup.string().required().trim()
          .matches(FINANCIAL_INSTITUTION_REGEX)
          .min(FINANCIAL_INSTITUTION_MIN_LENGTH)
          .max(FINANCIAL_INSTITUTION_MAX_LENGTH) }) });

  const initialValues: OpenIkeIkzeKorpoDataForm = {
    korpoCode: '',
    operationType: 'open' as OpenIkeIkzeKorpoOperationType,
    disclaimer: false,
    financialInstitution: '' };

  const processKorpoCodeError = (formikHelpers: FormikHelpers<OpenIkeIkzeKorpoDataForm>, status: ApiKorpoCodeResponseStatus) => {
    formikHelpers.setFieldError('korpoCode', status === 'USED'
        ? t('ike-ikze-korpo.korpo-code.code-used')
        : t('ike-ikze-korpo.korpo-code.code-error'));
    formikHelpers.setSubmitting(false);
  };

  const onFormSubmit = (values: OpenIkeIkzeKorpoDataForm, formikHelpers: FormikHelpers<OpenIkeIkzeKorpoDataForm>) => {
    postKorpoCode(productType === ProductType.ike ? 'IKEK' : 'IKZEK', values.korpoCode || '')
        .then(response => {
          if (!validKorpoCodeResponseStatus.includes(response.status)) {
            processKorpoCodeError(formikHelpers, response.status);
            return;
          }
          dispatch(cartStateSlice.actions.clearCart());
          getApiEmployerProductFunds(response.productIdn, response.employerIdn).then(fundsResponse => {
            const unitCategoryCode = fundsResponse[0].shareClassIdn as UnitCategoryCode;
            dispatch(productWizardActions.setKorpoData({
              unitCategoryCode,
              korpoCode: values.korpoCode,
              productCode: productType,
              productIdn: response.productIdn,
              employerIdn: response.employerIdn,
              allowedFunds: fundsResponse.map(fundResponse => {
                const foundFund = allFunds.find(fund => fund.afsCodeForUnitCode[unitCategoryCode] === fundResponse.idn);
                return {
                  fundId: foundFund ? foundFund.id : undefined,
                  fundIdn: fundResponse.idn,
                  minPurchase: fundResponse.minPurchase,
                  minPurchaseOpening: fundResponse.minPurchaseOpening } as KorpoEmployerFundData;
              }),
              statementPl: response.statementPl,
              statementEn: response.statementEng }));

            dispatch(cartStateSlice.actions.setTransferInstitutionName(values.operationType === 'transfer' ? values.financialInstitution : undefined));
            switch (values.operationType) {
              case 'open':
                dispatch(cartStateSlice.actions.setRegisterMode(RegisterMode.OPEN_KORPO));
                break;
              case 'transfer':
                dispatch(cartStateSlice.actions.setRegisterMode(RegisterMode.EXT_TRANSFER_KORPO));
                break;
              case 'ppe_transfer':
                dispatch(cartStateSlice.actions.setRegisterMode(RegisterMode.PPE_TRANSFER_PAYMENT_KORPO));
                break;
              default:
                throw new Error();
            }
            const nextUrl = `/${url('product-wizard')}/${url('select-funds')}`;
            history.push(nextUrl);
          }).catch(() => {
            processKorpoCodeError(formikHelpers, 'ERR');
          });
        })
        .catch(() => {
          processKorpoCodeError(formikHelpers, 'ERR');
        });
  };

  const KorpoTextField = ({ disabled }: { disabled: boolean }) => {
    const [korpoCodeField, korpoCodeMeta] = useField('korpoCode');

    return (
      <TextField
        className={c.korpoCodeInput}
        testId="korpoCode"
        label={t('ike-ikze-korpo.korpo-code.label')}
        disabled={disabled}
        required={true}
        hasError={korpoCodeMeta.touched && !!korpoCodeMeta.error}
        error={korpoCodeMeta.touched && korpoCodeMeta.error}
        placeholder={t('ike-ikze-korpo.korpo-code.placeholder')}
        {...korpoCodeField}
      />
    );
  };

  const TransferDisclaimerBox = ({ disabled }: { disabled: boolean }) => {
    const [disclaimerField, disclaimerMeta] = useField('disclaimer');
    const [institutionField, institutionMeta] = useField('financialInstitution');
    const { getUniqueValue } = useUniqueWcagValues();
    const disclaimerId = getUniqueValue('open-ike-ikze-korpo-disclaimer-checkbox');

    return (
      <div className={c.disclaimerBox}>
        <FormControlLabel
          className={c.disclaimerFormControl}
          htmlFor={disclaimerId}
          label={t(`${productType.toLowerCase()}-korpo.disclaimer`)}
          control={
            <Checkbox
              id={disclaimerId}
              data-testid="disclaimer"
              disabled={disabled}
              checked={Boolean(disclaimerField.value)}
              hasError={disclaimerMeta.touched && !!disclaimerMeta.error}
              inputProps={{
                'aria-required': true
              }}
              {...disclaimerField}
            />
          }
        />
        <TextField
          className={c.financialInstitutionInput}
          testId="financialInstitution"
          label={t('ike-ikze-korpo.financial-institution.label')}
          disabled={disabled}
          hasError={institutionMeta.touched && !!institutionMeta.error}
          error={institutionMeta.touched && institutionMeta.error}
          placeholder={t('ike-ikze-korpo.financial-institution.placeholder')}
          {...institutionField}
        />
      </div>
    );
  };

  const hasError = (fieldName: string, checked: boolean, touched: FormikTouched<OpenIkeIkzeKorpoDataForm>, errors: FormikErrors<OpenIkeIkzeKorpoDataForm>) => {
    return !checked && _.get(touched, fieldName) && _.get(errors, fieldName);
  };

  return (
    <div className={c.wrapper}>
      <ProductWizardTitle
        onBackButtonClick={() => history.goBack()}
        titleKey={
          productType === ProductType.ike
          ? Screen.OPEN_IKE_KORPO.code
          : Screen.OPEN_IKZE_KORPO.code
        }
      />
      <Card className={c.root} fullWidth>
        <Label
          className={c.label}
          size="xl"
          color="dark"
          fontWeight='bold'
          hasBottomMargin
        >
          {t('ike-ikze-korpo.title-label')}
        </Label>
        <Formik<OpenIkeIkzeKorpoDataForm>
          validationSchema={validationSchema}
          initialValues={initialValues}
          onSubmit={onFormSubmit}>
          {({ isSubmitting, values, touched, errors, setFieldValue, isValid }) => {
            const handleRadioGroupChange = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
              setFieldValue('operationType', value);
            };
            const isFormComplete = () => {
              if (!values.korpoCode?.trim()) {
                return false;
              }
              if (!values.operationType) {
                return false;
              }
              if (values.operationType === 'transfer') {
                if (!values.disclaimer || !values.financialInstitution?.trim()) {
                  return false;
                }
              }
              return true;
            };

            const isButtonDisabled = isSubmitting || !isValid || !isFormComplete();

            return (
              <CustomForm autoComplete="off">
                <RadioGroup
                  name="operationType"
                  required={true}
                  ariaRequiredId={openIkeIkzeKorpoRadioGroup}
                  id={openIkeIkzeKorpoRadioGroup}
                  value={values.operationType}
                  onChange={handleRadioGroupChange}
                >
                  <FormControlLabel value="open"
                                    control={<Radio hasError={hasError('operationType', !!values.operationType, touched, errors)}/>}
                                    data-testid="open"
                                    label={t(`${productType.toLowerCase()}-korpo.radio-button.open`)}
                                    disabled={isSubmitting}/>
                  <FormControlLabel value="transfer"
                                    control={<Radio hasError={hasError('operationType', !!values.operationType, touched, errors)}/>}
                                    data-testid="transfer"
                                    label={t(`${productType.toLowerCase()}-korpo.radio-button.transfer`)}
                                    disabled={isSubmitting}/>
                  {values.operationType === 'transfer' && <TransferDisclaimerBox disabled={isSubmitting} />}
                  {productType === ProductType.ike
                      && <FormControlLabel  value="ppe_transfer"
                                            control={<Radio hasError={hasError('operationType', !!values.operationType, touched, errors)}/>}
                                            data-testid="ppe_transfer"
                                            label={t(`${productType.toLowerCase()}-korpo.radio-button.ppe_transfer`)}
                                            disabled={isSubmitting}
                                            />}
                </RadioGroup>
                <div className={c.korpoCodeBox}>
                  <Typography className={c.codeLabel}>{t('ike-ikze-korpo.korpo-code.description')}</Typography>
                  <KorpoTextField disabled={isSubmitting} />
                </div>
                <div className={c.buttonContainer}>
                  <Button
                    type="submit"
                    buttonType='primary'
                    accent={true}
                    isLoading={isSubmitting}
                    disabled={isButtonDisabled}
                  >
                    {tt('next')}
                  </Button>
                  <Button
                    buttonType='secondary'
                    onClick={() => history.goBack()}
                    width="auto"
                  >
                    {tt('cancel')}
                  </Button>
                </div>
              </CustomForm>
            );
          }}
        </Formik>
      </Card>
    </div>
  );
};

export default OpenIkeIkzeKorpo;
